import _ from 'lodash';

import {DashboardHistoryReportType} from '../components/dashboard-panels/DashboardPanelItem';
import {DashboardEntity} from '../interfaces/Dashboard';
import {PanelCode} from './panels';

export type HistoryReportId = number | string | number[] | string[];

export type HistoryReportExtraParams = Record<
  string,
  string | number | boolean | null | undefined
>;

interface HistoryReportMapping {
  panelCode: PanelCode;
  viewType: string;
}

const TYPE_TO_PANEL: Record<DashboardHistoryReportType, HistoryReportMapping> = {
  asset: {panelCode: 'CommtracNodeTrackingReports', viewType: 'assets'},
  employee: {panelCode: 'CommtracNodeTrackingReports', viewType: 'employees'},
  cn: {panelCode: 'NodeTrackingReports', viewType: 'cn'},
  wifi: {panelCode: 'NodeTrackingReports', viewType: 'wifi'},
  nodes: {panelCode: 'NodeTrackingReports', viewType: 'nodes'},
  wifiLongTerm: {panelCode: 'NodeTrackingReports', viewType: 'wifiLongTerm'},
  commtracNodeByCn: {
    panelCode: 'NodeTrackingReports',
    viewType: 'commtracNodeByCn',
  },
  networkDiagnostics: {
    panelCode: 'NetworkDiagnostics',
    viewType: 'networkDiagnostics',
  },
  alarm: {panelCode: 'AlarmHistoryReports', viewType: 'alarm'},
  alarm_log: {panelCode: 'AlarmLogReports', viewType: 'alarm_log'},
  hazard_ai_detection_log: {
    panelCode: 'HazardAIDectionLog',
    viewType: 'hazard_ai_detection_log',
  },
  hazard_ai_heatmap: {
    panelCode: 'HazardHeatMapReports',
    viewType: 'hazard_ai_heatmap',
  },
  hazard_ai_machine_location: {
    panelCode: 'HazardAIMachineLocationHistory',
    viewType: 'hazard_ai_machine_location',
  },
  amsShortTerm: {panelCode: 'GasMonitoringReport', viewType: 'amsShortTerm'},
  amsLongTerm: {panelCode: 'GasMonitoringReport', viewType: 'amsLongTerm'},
  amsLocation: {panelCode: 'GasMonitoringReport', viewType: 'amsLocation'},
  amsEmoduleSensorHistory: {
    panelCode: 'AmsEmoduleSensorHistory',
    viewType: 'amsEmoduleSensorHistory',
  },
  amsEmoduleInstallationHistory: {
    panelCode: 'AmsEmoduleInstallationHistory',
    viewType: 'amsEmoduleInstallationHistory',
  },
  amsEmoduleCalibration: {
    panelCode: 'AMSEModuleCalibration',
    viewType: 'amsEmoduleCalibration',
  },
  beltHistoryReport: {
    panelCode: 'BeltHistoryReport',
    viewType: 'beltHistoryReport',
  },
  beltLocationHistoryReport: {
    panelCode: 'BeltLocationHistoryReport',
    viewType: 'beltLocationHistoryReport',
  },
  machine_detections_by_camera: {
    panelCode: 'MachineDetectionsByCamera',
    viewType: 'machine_detections_by_camera',
  },
};

const serializeId = (id: HistoryReportId): string => {
  if (_.isArray(id)) {
    return id.join('-');
  }
  return String(id);
};

const appendExtraParams = (
  search: URLSearchParams,
  extra?: HistoryReportExtraParams
) => {
  if (!extra) {
    return;
  }
  for (const [key, value] of Object.entries(extra)) {
    if (value === null || value === undefined || value === '') {
      continue;
    }
    search.set(key, String(value));
  }
};

export const getHistoryReportPanelCode = (
  type: DashboardHistoryReportType
): PanelCode | undefined => TYPE_TO_PANEL[type]?.panelCode;

export const buildHistoryReportUrl = (
  type: DashboardHistoryReportType,
  id: HistoryReportId,
  extra?: HistoryReportExtraParams
): string => {
  const mapping = TYPE_TO_PANEL[type];
  if (!mapping) {
    return '#';
  }
  const search = new URLSearchParams();
  search.set('type', type);
  search.set('id', serializeId(id));
  appendExtraParams(search, extra);
  return `/panels/${mapping.panelCode}?${search.toString()}`;
};

export const buildPanelDrilldownUrl = (
  panelCode: PanelCode,
  extra?: HistoryReportExtraParams
): string => {
  const search = new URLSearchParams();
  appendExtraParams(search, extra);
  const qs = search.toString();
  return qs ? `/panels/${panelCode}?${qs}` : `/panels/${panelCode}`;
};

interface SeededEntity {
  panelCode: PanelCode;
  data: Record<string, any>;
}

const parseIdValue = (raw: string): number | string => {
  const asNumber = Number(raw);
  if (!Number.isNaN(asNumber) && raw.trim() !== '' && String(asNumber) === raw) {
    return asNumber;
  }
  return raw;
};

const buildOpenedItemParams = (
  extra: Record<string, string>
): Record<string, any> => {
  const params: Record<string, any> = {};
  for (const [key, value] of Object.entries(extra)) {
    if (key === 'type' || key === 'id') {
      continue;
    }
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber) && String(asNumber) === value) {
      params[key] = asNumber;
    } else {
      params[key] = value;
    }
  }
  return params;
};

export const buildHistoryReportSeedFromSearch = (
  panelCode: PanelCode,
  search: URLSearchParams
): SeededEntity | null => {
  const type = search.get('type') as DashboardHistoryReportType | null;
  const idRaw = search.get('id');
  const extra: Record<string, string> = {};
  search.forEach((value, key) => {
    extra[key] = value;
  });

  if (!type || idRaw === null) {
    const params = buildOpenedItemParams(extra);
    if (!Object.keys(params).length) {
      return null;
    }
    return {panelCode, data: {params}};
  }

  const mapping = TYPE_TO_PANEL[type];
  if (!mapping || mapping.panelCode !== panelCode) {
    return null;
  }

  const {viewType} = mapping;
  const isAMSReport = ['amsShortTerm', 'amsLongTerm', 'amsLocation'].includes(
    viewType
  );

  if (isAMSReport && panelCode === 'GasMonitoringReport') {
    const id = idRaw;
    return {
      panelCode,
      data: {
        viewType,
        activeId: id,
        openedItems: [id],
        amsShortTerm: {
          tabs:
            viewType === 'amsShortTerm'
              ? {
                  [id]: {
                    id,
                    selectAll: true,
                    chartMode: 'value',
                    columnWidths: {},
                  },
                }
              : {},
        },
        amsLongTerm: {
          tabs:
            viewType === 'amsLongTerm'
              ? {
                  [id]: {
                    id,
                    selectAll: true,
                    columnWidths: {},
                  },
                }
              : {},
        },
        amsLocation: {
          mapLayers: ['mine', 'ams_location'],
          mapLevel: null,
          tabs:
            viewType === 'amsLocation'
              ? {
                  [id]: {
                    id,
                    selectAll: true,
                    columnWidths: {},
                  },
                }
              : {},
        },
      },
    };
  }

  if (type === 'hazard_ai_machine_location') {
    return {
      panelCode,
      data: {
        viewType,
        [viewType]: {
          selectedMachineId: parseIdValue(idRaw),
        },
      },
    };
  }

  if (type === 'machine_detections_by_camera') {
    return {panelCode, data: {params: {machine_id: parseIdValue(idRaw)}}};
  }

  const id = parseIdValue(idRaw);
  const openedItemParams = buildOpenedItemParams(extra);
  const openedItem: Record<string, any> = {id};
  if (Object.keys(openedItemParams).length) {
    openedItem.params = openedItemParams;
  }

  return {
    panelCode,
    data: {
      viewType,
      [viewType]: {
        activeId: id,
        openedItems: [openedItem],
      },
    },
  };
};

export const buildHistoryReportEntity = (
  panelCode: PanelCode,
  search: URLSearchParams
): DashboardEntity | null => {
  const seeded = buildHistoryReportSeedFromSearch(panelCode, search);
  if (!seeded) {
    return null;
  }
  return {
    id: '1',
    type: 'panel',
    name: 'Fake',
    panel: {
      code: seeded.panelCode,
      data: seeded.data,
    },
    grid: {x: 0, y: 0, w: 12, h: 4},
  };
};
